import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calculator, Copy, Check, Shield, BookOpen, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import {
  CalculationResult,
  calcularVencimientoTerminos,
  calcularDosificacionPunitiva
} from "@/lib/calculators/calculatorsEngine";
import { CALCULATOR_FAQS } from "@/lib/calculators/calculatorFaqs";
import { deductCalculatorCredit } from "@/lib/calculators/deductCalculatorCredit";

// 1. Vencimiento de Términos Penales
export const VencimientoTerminosForm: React.FC = () => {
  const [jurisdiccion, setJurisdiccion] = useState("ordinario");
  const [gaoExtension, setGaoExtension] = useState(false);
  const [evento, setEvento] = useState("imputacion_acusacion");
  const [fechaEvento, setFechaEvento] = useState("2024-02-10");
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const d1 = new Date(fechaEvento);
    const d2 = new Date();
    const diasTranscurridos = Math.max(1, Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)));

    const res = calcularVencimientoTerminos(
      evento as any,
      diasTranscurridos,
      jurisdiccion === "especializado" || gaoExtension
    );
    setResult(res);
  };

  const faqs = CALCULATOR_FAQS["vencimiento-terminos"] || [
    {
      question: "¿Cuáles son los términos para solicitar la libertad por vencimiento?",
      answer: "Art. 317 Causal 4: 60 días de imputación a escrito de acusación. Causal 5: 120 días de acusación a inicio de juicio oral. Causal 6: 150 días de inicio de juicio oral a sentido del fallo."
    },
    {
      question: "¿Cuándo se duplican los plazos penales?",
      answer: "Cuando concurran 3 o más imputados, o el proceso curse ante la justicia penal especializada o por delitos de corrupción."
    }
  ];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Art. 317 CPP</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Regula las causales de libertad por vencimiento de términos procesales en medida de aseguramiento.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Términos Duplicados</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Justicia especializada o 3+ acusados amplía plazos a 120, 240 o 300 días.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Maniobras Dilatorias</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Se descuentan del cómputo los días de suspensión atribuibles a la bancada defensiva.
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-red-600" /> Parámetros del Proceso Penal
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Jurisdicción / Tipo de Juzgado *</Label>
            <Select value={jurisdiccion} onValueChange={setJurisdiccion}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                <SelectItem value="ordinario">Ordinario (Ley 906/2004)</SelectItem>
                <SelectItem value="municipal">Juez Penal Municipal</SelectItem>
                <SelectItem value="especializado">Juez Especializado (Términos Duplicados)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Evento Procesal de Referencia *</Label>
            <Select value={evento} onValueChange={setEvento}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                <SelectItem value="imputacion_acusacion">Imputación a Escrito de Acusación (60 días - Art. 175)</SelectItem>
                <SelectItem value="acusacion_juicio">Acusación a Inicio de Juicio Oral (120 días - Art. 317-5)</SelectItem>
                <SelectItem value="juicio_fallo">Juicio Oral a Sentido del Fallo (150 días - Art. 317-6)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha del Evento de Referencia *</Label>
            <Input
              type="date"
              value={fechaEvento}
              onChange={(e) => setFechaEvento(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
        </div>

        <div className="flex items-center space-x-2 pt-2">
          <Checkbox id="gao" checked={gaoExtension} onCheckedChange={(c) => setGaoExtension(Boolean(c))} />
          <Label htmlFor="gao" className="text-xs text-slate-700 cursor-pointer">
            GAO / GAOR / GDO: Proceso por Grupos Armados (términos extendidos Art. 317 par. 2)
          </Label>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Calculator className="w-4 h-4 mr-2" /> Verificar Vencimiento de Términos (Art. 317 CPP) — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <PenalResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-red-600" /> Preguntas Frecuentes Penales
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// 2. Dosificación Punitiva
export const DosificacionPunitivaForm: React.FC = () => {
  const [penaMinima, setPenaMinima] = useState("96"); // 8 años
  const [penaMaxima, setPenaMaxima] = useState("216"); // 18 años
  const [cuarto, setCuarto] = useState<"minimo" | "medio_inferior" | "medio_superior" | "maximo">("minimo");
  const [atenuantes, setAtenuantes] = useState(true);
  const [agravantes, setAgravantes] = useState(false);
  const [allanamientoPct, setAllanamientoPct] = useState("50");
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const min = parseFloat(penaMinima) || 1;
    const max = parseFloat(penaMaxima) || 12;
    const reb = parseFloat(allanamientoPct) || 0;

    const res = calcularDosificacionPunitiva(min, max, cuarto, reb);
    setResult(res);
  };

  const faqs = CALCULATOR_FAQS["dosificacion-punitiva"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Art. 61 Ley 599/2000</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Divide la pena en cuatro partes (Cuarto Mínimo, Medios y Máximo).
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Atenuantes y Agravantes</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Determinan el cuarto aplicable: Solo atenuantes ubica el cálculo en el cuarto mínimo.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Allanamiento / Preacuerdo</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Aplica rebaja porcentual sobre la pena individualizada en el cuarto respetivo.
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-red-600" /> Tipo Penal y Modificadores
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Pena Mínima (meses) *</Label>
            <Input
              type="number"
              value={penaMinima}
              onChange={(e) => setPenaMinima(e.target.value)}
              placeholder="Ej: 96 (8 años)"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Pena Máxima (meses) *</Label>
            <Input
              type="number"
              value={penaMaxima}
              onChange={(e) => setPenaMaxima(e.target.value)}
              placeholder="Ej: 216 (18 años)"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Cuarto Punitivo Seleccionado *</Label>
            <Select value={cuarto} onValueChange={(v: any) => setCuarto(v)}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                <SelectItem value="minimo">Cuarto Mínimo (Solo atenuantes)</SelectItem>
                <SelectItem value="medio_inferior">Cuarto Medio Inferior</SelectItem>
                <SelectItem value="medio_superior">Cuarto Medio Superior</SelectItem>
                <SelectItem value="maximo">Cuarto Máximo (Solo agravantes)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">% Rebaja por Allanamiento / Preacuerdo</Label>
            <Input
              type="number"
              value={allanamientoPct}
              onChange={(e) => setAllanamientoPct(e.target.value)}
              placeholder="50% imputación, 33.3% acusación..."
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <div className="flex items-center space-x-2">
            <Checkbox id="at" checked={atenuantes} onCheckedChange={(c) => setAtenuantes(Boolean(c))} />
            <Label htmlFor="at" className="text-xs text-slate-700 cursor-pointer">
              Circunstancias de atenuación punitiva (Arts. 54–55 Ley 599/2000)
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="ag" checked={agravantes} onCheckedChange={(c) => setAgravantes(Boolean(c))} />
            <Label htmlFor="ag" className="text-xs text-slate-700 cursor-pointer">
              Circunstancias de agravación punitiva (Arts. 57–58 Ley 599/2000)
            </Label>
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Calculator className="w-4 h-4 mr-2" /> Dosificar Pena Imponible (Ley 599/2000) — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <PenalResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-red-600" /> Preguntas Frecuentes & Sistema de Cuartos
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PenalResultDisplay: React.FC<{ result: CalculationResult }> = ({ result }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = `--- INFORME DE DOSIFICACIÓN PUNITIVA ---
${result.title}
${result.summary}

PENA DEFINITIVA: ${result.total} Meses (${(result.total / 12).toFixed(1)} Años)

DESGLOSE:
${result.breakdown.map((b) => `- ${b.label}: ${b.value}`).join("\n")}

CITA NORMATIVA:
${result.normativa.map((n) => `- ${n}`).join("\n")}

Generado en Mi Asistente Legalia`;

    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Informe procesal copiado");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6 rounded-2xl bg-red-50/60 border border-red-200 space-y-6 text-slate-900 shadow-sm animate-in fade-in">
      <div className="flex justify-between items-start">
        <div>
          <span className="text-[11px] font-extrabold text-red-800 uppercase tracking-wider">
            Resumen de Pena Individualizada
          </span>
          <div className="text-3xl md:text-4xl font-black text-slate-900 mt-1">
            {typeof result.total === "number" ? `${result.total.toFixed(1)} Meses (${(result.total / 12).toFixed(1)} Años)` : result.total}
          </div>
          <p className="text-xs text-slate-600 mt-1">{result.summary}</p>
        </div>
        <Button onClick={handleCopy} variant="outline" size="sm" className="bg-white border-slate-300 text-slate-700 hover:bg-slate-50 font-bold">
          {copied ? <Check className="w-4 h-4 text-red-600 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
          {copied ? "Copiado!" : "Copiar Informe"}
        </Button>
      </div>

      {/* Breakdown Table */}
      <div className="space-y-2">
        <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
          <BookOpen className="w-4 h-4 text-red-700" /> Desglose de Cuartos y Rebajas
        </h4>
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
          {result.breakdown.map((b, i) => (
            <div key={i} className="p-3.5 flex justify-between text-xs">
              <span className="text-slate-600 font-medium">{b.label}</span>
              <span className="font-bold text-slate-900">{b.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Normativa */}
      <div className="p-4 bg-white rounded-xl border border-slate-200 space-y-2">
        <h5 className="text-xs font-bold text-red-800 uppercase tracking-wider">Fundamento Legal (Ley 599 / Ley 906)</h5>
        <ul className="text-xs text-slate-600 list-disc pl-4 space-y-1">
          {result.normativa.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};
